"use client";

import { useMemo } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { useStep } from "@/hooks/use-step-handler";
import { RetailClientStep } from "@/components/backoffice/cat/vehicle-assignment/client-step/retail/retail-client-content";
import { RetailVehicleBodyStep } from "@/components/backoffice/cat/vehicle-assignment/vehicle-bodywork-step/vehicle-bodywork-content";
import VehicleIdentification from "@/components/backoffice/cat/vehicle-assignment/vehicle-identification-step/vehicle-identification-content";
import { RetailVehicleTradeInStep } from "@/components/backoffice/cat/vehicle-assignment/vehicle-trade-in-step/vehicle-trade-in-content";
import { getRetailVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-retail-step-definitions";
import type { VehicleAssignmentFlowContext } from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";
import { RetailAdditionalInformationsStep } from "@/components/backoffice/cat/vehicle-assignment/additional-informations-step/retail/additional-informations-step-content";
import { RetailFundingStep } from "@/components/backoffice/cat/vehicle-assignment/funding/funding-step";
import { RetailSaleTypeStep } from "../sale-type-step/retail/retail-sale-type-content";
import { RetailSummaryValidationStep } from "@/components/backoffice/cat/vehicle-assignment/summary-validation-step/retail/retail-summary-validation-step-content";
import { VehicleAssignmentStepErrorProvider } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-error";

function VehicleAssignmentRetailContent({
  currentKvps,
  onCanProceedChange,
  onBeforeNextChange,
  onGoToPreviousStep,
}: {
  currentKvps?: Kvps | null;
  onCanProceedChange?: (canProceed: boolean) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
  onGoToPreviousStep?: (targetStep: number) => void;
}) {
  const { step } = useStep();

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({ kvps: currentKvps ?? null }),
    [currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getRetailVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const def = visibleDefinitions[step - 1];

  const stepContent = (() => {
    if (!def) {
      return (
        <Card>
          <CardContent className="p-6 text-gray-600">
            Étape invalide (WIP)
          </CardContent>
        </Card>
      );
    }

    switch (def.id) {
      case "vehicle_identification":
        return (
          <VehicleIdentification
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
            currentKvps={currentKvps}
          />
        );
      case "client":
        return (
          <RetailClientStep
            currentKvps={currentKvps}
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
          />
        );
      case "vehicle_bodywork":
        return (
          <RetailVehicleBodyStep
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
          />
        );
      case "vehicle_trade-in":
        return (
          <RetailVehicleTradeInStep
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
          />
        );
      case "additional_informations":
        return (
          <RetailAdditionalInformationsStep
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
            currentKvps={currentKvps}
          />
        );
      case "leasing_financing":
        return (
          <RetailFundingStep
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
          />
        );
      case "selling_type_retail":
        return (
          <RetailSaleTypeStep
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
          />
        );
      case "ctm_summary_validation":
        return (
          <RetailSummaryValidationStep
            currentKvps={currentKvps}
            onCanProceedChange={onCanProceedChange}
            onBeforeNextChange={onBeforeNextChange}
            onGoToPreviousStep={onGoToPreviousStep}
          />
        );
      default:
        return (
          <Card>
            <CardContent className="p-6 text-gray-600">
              {`Vous essayer d'accéder à une étape qui n&apos;existe pas : ${def.id}.`}
            </CardContent>
          </Card>
        );
    }
  })();

  return (
    <VehicleAssignmentStepErrorProvider>
      {stepContent}
    </VehicleAssignmentStepErrorProvider>
  );
}

export { VehicleAssignmentRetailContent };
